import { config } from '@uhg-abyss/web/tools/config';
import { useEffect, useState } from 'react';

import { Constants } from '../../common/Constants';
import { getCloudData } from '../../lagoon/lagoonUtil';
import { getCookieValue } from '../../utils/cookies.utils';

/**
 * Maps a plan line of business to its guest Lagoon LOB path so that a
 * feature flag can be read from the LOB specific feature-flags table
 * (e.g. EI => guest-eni, CNS => guest-cns).
 */
export const getGuestLagoonLob = (lineOfBusiness?: string): string => {
  const normalizedLob =
    lineOfBusiness?.toUpperCase().replace(/[&\s]/g, '') || '';

  switch (normalizedLob) {
    case 'CS':
    case 'CNS':
    case 'MEDICAID':
      return 'guest-cns';
    case 'MR':
    case 'MNR':
      return 'guest-mnr';
    case 'IFP':
      return 'guest-ifp';
    case 'EI':
    case 'ENI':
    default:
      return 'guest-eni';
  }
};

/**
 * Loads the Lagoon cloud data for a plan's guest LOB (e.g. EI => guest-eni,
 * CNS => guest-cns). Reusable to read any table (feature-flags, config,
 * ui-messaging, ...) for a given line of business.
 */
export const getGuestLobCloudData = async (lineOfBusiness?: string) => {
  // Detect the prod-unofficial (pre-prod) environment from cookie
  const psxIsProdUnofficial =
    getCookieValue('psxIsProdUnofficial') === 'true' || false;
  // Resolve which Lagoon environment folder to read from
  const env = psxIsProdUnofficial ? 'prod-unofficial' : config('LAGOON_ENV');
  const lob = getGuestLagoonLob(lineOfBusiness);

  return getCloudData(lob, env, 'en');
};

/**
 * Reads a feature flag from the LOB specific guest Lagoon feature-flags table
 * (e.g. EI => guest-eni, CNS => guest-cns) by reusing `getGuestLobCloudData`.
 * Reusable for any flag that must be resolved per line of business independent
 * of the current Lagoon context.
 */
export const useGuestLobFeatureFlag = (
  flagKey: string,
  lineOfBusiness?: string
): boolean => {
  const [isFlagEnabled, setIsFlagEnabled] = useState(false);

  useEffect(() => {
    // Guard against setting state after the component unmounts
    let isMounted = true;

    const fetchFlag = async () => {
      const cloudData = await getGuestLobCloudData(lineOfBusiness);
      const flagEntry =
        cloudData?.[Constants.LAGOON_TABLE.FEATURE_FLAGS]?.[flagKey];

      if (isMounted) {
        setIsFlagEnabled(!!flagEntry?.active);
      }
    };

    fetchFlag();

    return () => {
      isMounted = false;
    };
  }, [flagKey, lineOfBusiness]);

  return isFlagEnabled;
};
